"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Progress } from '../../ui/progress'
import { cn } from '../../../lib/utils'
import { ProgressData } from '../types'
import { Clock, TrendingUp, Target } from 'lucide-react'

interface ProgressWidgetProps {
  data: ProgressData | ProgressData[]
  title?: string
  className?: string
  glassmorphism?: boolean
  compact?: boolean
}

export function ProgressWidget({
  data,
  title = "Progress",
  className,
  glassmorphism = false,
  compact = false
}: ProgressWidgetProps) {
  const items = Array.isArray(data) ? data : [data]
  const [isMounted, setIsMounted] = React.useState(false)

  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <Card className={cn(
      "h-full flex flex-col",
      glassmorphism && "bg-background/60 backdrop-blur-md border-white/10",
      className
    )}>
      <CardHeader className="pb-3 space-y-0">
        <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2">
          <Target className="h-4 w-4 text-muted-foreground" />
          <span className="truncate">{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 overflow-auto">
        {items.map((item, index) => {
          const progress = (item.current / item.target) * 100
          const isOverdue = item.deadline && new Date(item.deadline) < new Date()
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="space-y-2"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium truncate">{item.title}</h4>
                  {item.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
                  )}
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xs sm:text-sm font-semibold tabular-nums">
                    {item.current} / {item.target} {item.unit}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {progress.toFixed(1)}%
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <Progress 
                  value={Math.min(progress, 100)} 
                  className={cn(
                    "h-1.5 sm:h-2",
                    isOverdue && "[&>div]:bg-destructive"
                  )}
                />
                {item.milestone && (
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 sm:h-4 bg-border"
                    style={{ left: `${(item.milestone / item.target) * 100}%` }}
                  />
                )}
              </div>
              
              {item.deadline && (
                <div className={cn(
                  "flex items-center gap-1 text-xs",
                  isOverdue ? "text-destructive" : "text-muted-foreground"
                )}>
                  <Clock className="h-3 w-3" />
                  <span>
                    {isOverdue ? 'Overdue' : 'Due'}: {isMounted ? new Date(item.deadline).toLocaleDateString() : 'Loading...'}
                  </span>
                </div>
              )}
              
              {item.trend && (
                <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                  <TrendingUp className="h-3 w-3" />
                  <span>+{item.trend}% this week</span>
                </div>
              )}
            </motion.div>
          )
        })}
      </CardContent>
    </Card>
  )
}